import { Card } from "@astryxdesign/core/Card";
import { Center } from "@astryxdesign/core/Center";
import { EmptyState } from "@astryxdesign/core/EmptyState";
import { Grid } from "@astryxdesign/core/Grid";
import { Link } from "@astryxdesign/core/Link";
import { Section } from "@astryxdesign/core/Section";
import { Spinner } from "@astryxdesign/core/Spinner";
import { HStack, VStack } from "@astryxdesign/core/Stack";
import { StatusDot } from "@astryxdesign/core/StatusDot";
import { Switch } from "@astryxdesign/core/Switch";
import { Text } from "@astryxdesign/core/Text";
import { useToast } from "@astryxdesign/core/Toast";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Navigate } from "@tanstack/react-router";
import { AppFrame } from "../components/AppFrame";
import { api, ApiError, type SiteRow } from "../lib/api";
import { PowerIcon } from "../lib/icons";

const sitesKey = ["sites"] as const;

/**
 * The kill switch for every game subdomain.
 *
 * Switch ON = live. Switching a game off writes a KV flag its Worker reads on every
 * request: the subdomain starts serving a "retired" 410 page and the hub greys the card.
 * Games cache the flag for 60s, so a flip takes up to a minute to be visible everywhere.
 */
export function SitesPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const { data, error, isPending } = useQuery({
    queryKey: sitesKey,
    queryFn: api.listSites,
    staleTime: 30_000,
  });

  const toggle = useMutation({
    mutationKey: ["setSiteRetired"],
    mutationFn: ({ id, retired }: { id: string; retired: boolean }) =>
      api.setSiteRetired(id, retired),
    // Optimistic: the switch should move under the thumb, not after a round trip.
    onMutate: async ({ id, retired }) => {
      await qc.cancelQueries({ queryKey: sitesKey });
      const prev = qc.getQueryData<{ sites: SiteRow[] }>(sitesKey);
      qc.setQueryData<{ sites: SiteRow[] }>(sitesKey, (cur) =>
        cur ? { sites: cur.sites.map((s) => (s.id === id ? { ...s, retired } : s)) } : cur,
      );
      return { prev };
    },
    onError: (_err, vars, ctx) => {
      if (ctx?.prev) qc.setQueryData(sitesKey, ctx.prev);
      toast({ body: `Could not switch ${vars.id}`, type: "error", uniqueID: "sites" });
    },
    onSuccess: (res) => {
      toast({
        body: res.retired ? `${res.id} is off — retired page live` : `${res.id} is back on`,
        uniqueID: "sites",
      });
    },
  });

  if (error instanceof ApiError && error.status === 401) return <Navigate to="/login" />;

  const sites = data?.sites ?? [];
  const liveCount = sites.filter((s) => !s.retired).length;
  const retiredCount = sites.length - liveCount;

  // Grouped by kind, retired sunk to the bottom of each group. With games and tools
  // together the list is long enough that one flat column stops being scannable.
  const byKind = (kind: SiteRow["kind"]) =>
    sites
      .filter((s) => s.kind === kind)
      .sort((a, b) => Number(a.retired) - Number(b.retired) || a.label.localeCompare(b.label));

  const row = (s: SiteRow) => (
    <SiteListRow
      key={s.id}
      site={s}
      isBusy={toggle.isPending && toggle.variables?.id === s.id}
      onToggle={(checked) => toggle.mutate({ id: s.id, retired: !checked })}
    />
  );

  const group = (title: string, rows: SiteRow[]) =>
    rows.length === 0 ? null : (
      <VStack gap={1}>
        <HStack gap={1} align="center">
          <Text type="label" color="secondary">
            {title}
          </Text>
          <Text type="supporting" size="xsm" color="secondary">
            {rows.filter((s) => !s.retired).length} of {rows.length} live
          </Text>
        </HStack>
        <Section padding={0}>
          <VStack as="ul" gap={0} paddingBlock={1}>
            {rows.map(row)}
          </VStack>
        </Section>
      </VStack>
    );

  return (
    <AppFrame active="sites">
      <Center axis="horizontal">
        <VStack gap={3} maxWidth={760} width="100%" padding={4}>
          {isPending ? (
            <Center minHeight={240}>
              <Spinner size="lg" label="Loading" />
            </Center>
          ) : error ? (
            <Center minHeight={240}>
              <EmptyState
                icon={<PowerIcon fontSize={40} />}
                title="Could not load sites"
                description="The kill switch could not reach KV. Try again in a moment."
              />
            </Center>
          ) : (
            <>
              <Grid columns={2} gap={2}>
                <Card padding={3}>
                  <VStack gap={0.5}>
                    <Text type="supporting" size="xsm" color="secondary">
                      Live
                    </Text>
                    <Text type="body" weight="bold" size="lg" color="accent">
                      {liveCount}
                    </Text>
                  </VStack>
                </Card>
                <Card padding={3}>
                  <VStack gap={0.5}>
                    <Text type="supporting" size="xsm" color="secondary">
                      Retired
                    </Text>
                    <Text type="body" weight="bold" size="lg">
                      {retiredCount}
                    </Text>
                  </VStack>
                </Card>
              </Grid>

              {group("Games", byKind("game"))}
              {group("Tools", byKind("tool"))}

              <Text type="supporting" size="xsm" color="secondary">
                Switching a game off takes up to 60 seconds to show everywhere. Scores stay in
                the database either way, so flipping it back restores the leaderboard untouched.
              </Text>
            </>
          )}
        </VStack>
      </Center>
    </AppFrame>
  );
}

interface RowProps {
  site: SiteRow;
  isBusy: boolean;
  onToggle: (checked: boolean) => void;
}

/**
 * One subdomain row: status, name, blurb, an escape hatch to open it, and the switch.
 *
 * The row itself is not clickable — a clickable row with a Switch inside it would nest
 * two click targets. The origin link is its own control instead.
 */
function SiteListRow({ site, isBusy, onToggle }: RowProps) {
  return (
    <HStack as="li" gap={2} align="center" paddingInline={3} paddingBlock={1.5}>
      <StatusDot
        variant={site.retired ? "neutral" : "success"}
        label={site.retired ? "Retired" : "Live"}
      />

      <VStack gap={0} width="100%">
        <HStack gap={1} align="center">
          <Text weight="medium">{site.label}</Text>
          <Link href={site.origin} isExternalLink>
            <Text type="supporting" size="xsm" color="secondary">
              {site.origin.replace("https://", "")}
            </Text>
          </Link>
        </HStack>
        <Text type="supporting" size="xsm" color="secondary" maxLines={1}>
          {site.blurb}
        </Text>
      </VStack>

      <Switch
        label={`${site.label} live`}
        isLabelHidden
        value={!site.retired}
        isLoading={isBusy}
        onChange={onToggle}
      />
    </HStack>
  );
}
